import React from 'react';
import { useTranslation } from 'react-i18next';

import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@swyftx/aviary/atoms/Accordion/Accordion';
import { Button } from '@swyftx/aviary/atoms/Button';
import { Body } from '@swyftx/aviary/atoms/Typography';

import { useIntercom } from 'react-use-intercom';

import { LoansSection } from '../common/LoansSection';

type Props = {
  className?: string;
};

const LoansFAQ: React.FC<Props> = () => {
  const { show } = useIntercom();
  const { t } = useTranslation('loans');

  // Generate FAQ IDs & Translations dynamically
  const faqIds = Array.from({ length: 10 }, (_, i) => `faq-${i + 1}` as const);
  type FaqId = (typeof faqIds)[number];
  const faqTranslations: Record<FaqId, { question: string; answer: string }> = Object.fromEntries(
    faqIds.map((id, i: number) => {
      const index = i + 1;
      const qKey = `FAQ.faqItems.${index}.question` as const;
      const aKey = `FAQ.faqItems.${index}.answer` as const;
      return [id, { question: qKey, answer: aKey }];
    }),
  ) as Record<FaqId, { question: string; answer: string }>;

  // Generate FAQ items
  const faqItems = Object.values(faqTranslations).map((item) => ({
    id: item.question,
    question: t(item.question as unknown as TemplateStringsArray),
    answer: t(item.answer as unknown as TemplateStringsArray),
  }));

  // Render FAQ accordion
  const faqContent = (
    <Accordion type='multiple' className='grid w-full gap-12'>
      {faqItems.map((faq) => (
        <AccordionItem key={faq.id} className='rounded-8 border-0 bg-color-background-neutral' value={faq.id}>
          <AccordionTrigger>
            <Body size='medium' color='primary' weight='emphasis' className='text-left'>
              {faq.question}
            </Body>
          </AccordionTrigger>
          <AccordionContent>
            <Body size='small' color='secondary'>
              {faq.answer}
            </Body>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );

  return (
    <LoansSection
      header={{
        title: t('FAQ.title'),
        description: t('FAQ.description'),
      }}
      content={faqContent}
      button={
        <Button className='justify-self-center' size='lg' onClick={show}>
          {t('FAQ.button.label')}
        </Button>
      }
    />
  );
};

LoansFAQ.displayName = 'LoansFAQ';

export { LoansFAQ };
